import React from 'react';
import { Parcel, SoilZones } from '../types';
import { CLASSES, CLASS_BY_ID, RULES } from '../constants/landUseConfig';
import { Info } from 'lucide-react';

interface Props {
  selectedParcel: Parcel | undefined;
  soilZones: SoilZones;
  onReassign: (parcelId: string, newClass: string) => void;
}

export const ParcelEditor: React.FC<Props> = ({ selectedParcel, soilZones, onReassign }) => {
  if (!selectedParcel) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
        <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-[10px] text-blue-900 leading-tight">
          <strong>Interactive:</strong> Click any parcel to inspect and reassign land use. 
          Toggle soil by clicking labels. Press <strong>RECOMPUTE</strong> to update metrics.
        </p>
      </div>
    );
  }

  const currentClass = CLASS_BY_ID[selectedParcel.cls];
  const currentRules = RULES[selectedParcel.cls];

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
      <div className="flex items-center gap-2 mb-2.5">
        <div className="px-2 py-0.5 bg-gray-100 rounded-full">
          <span className="text-[9px] font-mono font-semibold text-gray-700">
            {selectedParcel.id.toUpperCase()}
          </span>
        </div>
        <div className="text-[9px] text-gray-600">
          {selectedParcel.areaHa.toFixed(2)} ha · {soilZones[selectedParcel.soilZone].toUpperCase()} soil
        </div>
      </div>

      <div className="mb-3 pb-3 border-b border-gray-200">
        <p className="text-[10px] text-gray-600 mb-1.5">Current land use:</p>
        <div className="flex items-center gap-2">
          <div
            className="w-5 h-5 rounded border-2 flex-shrink-0"
            style={{ backgroundColor: currentClass.fill, borderColor: currentClass.ink }}
          />
          <div>
            <p className="text-xs font-semibold text-gray-900">{currentClass.label}</p>
            <p className="text-[9px] text-gray-500">
              €{currentRules.econ}/ha · {currentRules.carbon > 0 ? '+' : ''}{currentRules.carbon} tCO₂e · Bio {currentRules.bio}%
            </p>
          </div>
        </div>
      </div>

      <p className="text-[10px] font-semibold text-gray-700 mb-2">Reassign to:</p>
      <div className="grid grid-cols-3 gap-2">
        {CLASSES.map((c) => {
          const isActive = c.id === selectedParcel.cls;
          const rules = RULES[c.id];
          return (
            <button
              key={c.id}
              disabled={isActive}
              onClick={() => onReassign(selectedParcel.id, c.id)}
              className={`p-2 rounded border-2 text-left transition-all ${
                isActive
                  ? 'opacity-40 cursor-not-allowed bg-gray-50'
                  : 'hover:scale-105 hover:shadow-md active:scale-95 bg-white'
              }`}
              style={{
                borderColor: c.ink,
              }}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <div
                  className="w-3 h-3 rounded border flex-shrink-0"
                  style={{ backgroundColor: c.fill, borderColor: c.ink }}
                />
                <span className="text-[9px] font-semibold text-gray-900 leading-tight">{c.label.split(' ')[0]}</span>
              </div>
              <div className="text-[8px] text-gray-600 leading-tight">
                €{rules.econ} · {rules.carbon > 0 ? '+' : ''}{rules.carbon}CO₂ · {rules.bio}%
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};